import { Box, Flex, Spinner } from "@chakra-ui/react";
import { useEffect, useMemo, useRef } from "react";
import { ContentSectionCustomCard, NavigationSource } from "../../types";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../../utils/useBreakpoints";
import { CUSTOM_CARD_TYPES } from "../../components/cards/constants";
import { FeatureCardContainer } from "../../components/cards/FeatureCardContainer";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { getSection } from "../root/api/rootScreenApi";
import { ExpandedContentHeader } from "./components/ExpandedContentHeader";
import { ExpandedContentGrid } from "./components/ExpandedContentGrid";
import { useEntityCache } from "../../hooks/useEntityCache";
import { useSubHeaderScroll } from "../../hooks/useSubHeaderScroll";
import { useVirtualScrollRestoration } from "../../hooks/useVirtualScrollRestoration";

const SECTION_PER_PAGE = 50;

function extractItems(section: { articles?: any[]; artists?: any[]; comics?: any[]; releases?: any[]; cards?: any[]; bundles?: any[] } | null | undefined) {
  if (!section) return [];
  return [
    ...(section.articles ?? []),
    ...(section.artists ?? []),
    ...(section.comics ?? []),
    ...(section.releases ?? []),
    ...(section.cards ?? []),
    ...(section.bundles ?? []),
  ];
}

export function ExpandedContentScreen() {
  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { stickyTop: subHeaderTop, stickyTransform, stickyTransition } =
    useSubHeaderScroll();

  const q = searchParams.get("q") ?? "";

  // Scroll restoration for back navigation
  const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration();

  // Read navigation source from React Router state
  const navigationSource = location.state as NavigationSource | null;

  const { saveEntityToCache } = useEntityCache();

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    enabled: !!sectionId,
    queryKey: ["section", sectionId, q],
    queryFn: async ({ pageParam }) => {
      if (!sectionId) throw new Error("No sectionId");
      const response = await getSection(sectionId, {
        page: pageParam,
        perpage: SECTION_PER_PAGE,
        q: q || undefined,
      });
      const results = response.data.payload.results;
      if (results) {
        extractItems(results).forEach(saveEntityToCache);
      }
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage.data.meta as { page?: number; has_more?: boolean };
      return meta.has_more ? (meta.page ?? 1) + 1 : undefined;
    },
    placeholderData: keepPreviousData,
  });

  // Section metadata from first page — persist across search changes so header doesn't flash
  const latestSection = data?.pages[0]?.data.payload.results ?? null;
  const sectionRef = useRef(latestSection);
  if (latestSection) sectionRef.current = latestSection;
  const section = sectionRef.current;

  // Transform pages into the shape ResultsGrid expects
  const allPages = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.map((page) => {
      const items = extractItems(page.data.payload.results);
      return {
        data: {
          payload: {
            results: items,
            count: items.length,
            next: null,
            previous: null,
          },
        },
      };
    });
  }, [data?.pages]);

  // Flat items for header count
  const allItems = useMemo(
    () => allPages.flatMap((p) => p.data.payload.results),
    [allPages],
  );

  const style = section?.meta.layout.style;
  const { context } = style ?? {};

  useEffect(() => {
    if (!sectionId) {
      navigate("/");
    }
  }, [sectionId, navigate]);

  const isFeatureCard =
    context === CUSTOM_CARD_TYPES.featuredCarousel ||
    context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock;

  const isRoadblock = context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock;

  return (
    <Flex
      direction="column"
      gap={isMobile ? 0 : 6}
      width="full"
      sx={{
        WebkitOverflowScrolling: "touch",
      }}
    >
      {section && !isLoading ? (
        <>
          {isFeatureCard ? (
            <>
              <ExpandedContentHeader
                section={section}
                data={allItems}
                navigationSource={navigationSource}
              />
              <FeatureCardContainer
                data={allItems as ContentSectionCustomCard[]}
                scrollByCount={1}
                isRoadblock={isRoadblock}
              />
            </>
          ) : (
            <ExpandedContentGrid
              section={section}
              allPages={allPages as any}
              hasNextPage={hasNextPage ?? false}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              header={
                <Box
                  paddingRight={isSmall ? "0px" : "40px"}
                  position="sticky"
                  top={subHeaderTop}
                  transform={stickyTransform}
                  transition={stickyTransition}
                  zIndex={1}
                  bg="charcoal.charcoal"
                >
                  <ExpandedContentHeader
                    section={section}
                    data={allItems}
                    navigationSource={navigationSource}
                  />
                </Box>
              }
              onVisibleIndexChange={onVisibleIndexChange}
              restoreToIndex={restoreToIndex}
            />
          )}
        </>
      ) : (
        <Flex
          width="100%"
          height="100vh"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      )}
    </Flex>
  );
}
